import { useFormik } from 'formik';
import { pick } from 'lodash';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useDeleteEntityDocumentMutation,
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Alert, CustomCheckbox, Dropzone, TextInputField } from 'ui';
import { AlertBlueIcon } from 'ui/icons';

interface Document {
  data: string;
  type: string;
}

export enum AdvisorDocType {
  CompanyAdvisorDocument = 'company_advisor_document',
}

export interface CompanyAdviserDetails {
  is_authorized_representative: boolean;
  name_authorized_representative: string;
  role_held_with_investor: string;
  is_financial_adviser_nominated_by_investor: boolean;
  adviser: {
    name_of_adviser: string;
    afsl_number: string;
    dealer_group: string;
    name_of_advisory_firm: string;
    postal_address: string;
    suburb: string;
    postcode: string;
    email_address: string;
  };
  is_investor_received_advise: boolean;
  documents: Document[];
}

export const validationSchemaAdviserDetails = yup.object({
  is_authorized_representative: yup
    .boolean()
    .equals(
      [true],
      'You must be an authorised representative or agent as nominated by the investor(s)',
    )
    .required('This field is required'),
  name_authorized_representative: yup.string().trim().required('This field is required'),
  role_held_with_investor: yup.string().trim().required('This field is required'),
  is_financial_adviser_nominated_by_investor: yup.boolean().required('This field is required'),
  adviser: yup
    .object()
    .when(
      'is_financial_adviser_nominated_by_investor',
      ([is_financial_adviser_nominated_by_investor], schema) => {
        if (is_financial_adviser_nominated_by_investor) {
          return schema.shape({
            name_of_adviser: yup.string().trim().required('This field is required'),
            afsl_number: yup.string().trim().required('This field is required'),
            dealer_group: yup.string().trim().required('This field is required'),
            name_of_advisory_firm: yup.string().trim().required('This field is required'),
            postal_address: yup.string().trim().required('This field is required'),
            suburb: yup.string().trim().required('This field is required'),
            postcode: yup.string().trim().required('This field is required'),
            email_address: yup
              .string()
              .trim()
              .email('Please write correct email address')
              .required('This field is required'),
          });
        }
        return schema.shape({
          name_of_adviser: yup.string().trim(),
          afsl_number: yup.string().trim(),
          dealer_group: yup.string().trim(),
          name_of_advisory_firm: yup.string().trim(),
          postal_address: yup.string().trim(),
          suburb: yup.string().trim(),
          postcode: yup.string().trim(),
          email_address: yup.string().trim().email('Please write correct email address'),
        });
      },
    ),
  is_investor_received_advise: yup
    .boolean()
    .when(
      'is_financial_adviser_nominated_by_investor',
      ([is_financial_adviser_nominated_by_investor], schema) => {
        if (is_financial_adviser_nominated_by_investor) {
          return schema.isTrue('You must confirm');
        }
        return schema;
      },
    ),
  documents: yup
    .array()
    .min(1, 'Please upload document')
    .of(
      yup
        .object({
          data: yup.string().trim().required(),
          type: yup.string().trim().required(),
        })
        .required('Please upload document'),
    ),
});

export const initialValuesAdviserDetails: CompanyAdviserDetails = {
  is_authorized_representative: false,
  name_authorized_representative: '',
  role_held_with_investor: '',
  is_financial_adviser_nominated_by_investor: false,
  is_investor_received_advise: false,
  adviser: {
    name_of_adviser: '',
    afsl_number: '',
    dealer_group: '',
    name_of_advisory_firm: '',
    postal_address: '',
    suburb: '',
    postcode: '',
    email_address: '',
  },
  documents: [],
};

export const AdviserDetails = ({
  moveForward,
  entity_type_prefix,
  allowSkip,
}: OnboardingStepProps) => {
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');

  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const getInitialValues = () => {
    return {
      ...initialValuesAdviserDetails,
      ...data?.entity[entity_type_prefix as keyof typeof data.entity],
      adviser: {
        ...initialValuesAdviserDetails.adviser,
        ...data?.entity[entity_type_prefix as keyof typeof data.entity]?.adviser,
      },
    };
  };

  const formik = useFormik<CompanyAdviserDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaAdviserDetails,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      const initInfo = pick<CompanyAdviserDetails, keyof CompanyAdviserDetails>(values, [
        'is_financial_adviser_nominated_by_investor',
        'is_authorized_representative',
        'is_investor_received_advise',
        'role_held_with_investor',
        'name_authorized_representative',
        'adviser',
      ]);
      const entityTypeDetails = data?.entity[entity_type_prefix as keyof typeof data.entity];
      const currentInfo = pick<CompanyAdviserDetails, keyof CompanyAdviserDetails>(
        entityTypeDetails,
        [
          'is_financial_adviser_nominated_by_investor',
          'is_authorized_representative',
          'is_investor_received_advise',
          'role_held_with_investor',
          'name_authorized_representative',
          'adviser',
        ],
      );
      const initDoc =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];

      if (!isEqual(initInfo, currentInfo) || initDoc?.length > 0) {
        await updateEntity({
          ...data?.entity,
          documents: values.documents?.filter(
            item => !item.data?.toLowerCase()?.startsWith('http'),
          ),
          [entity_type_prefix]: {
            ...data?.entity[entity_type_prefix as keyof typeof data.entity],
            ...values,
            documents: null,
            adviser: {
              ...data?.entity[entity_type_prefix as keyof typeof data.entity]?.adviser,
              ...values.adviser,
            },
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation();

  const onFileAccepted = async (file: File) => {
    setIsFileLoading(true);

    const base64 = await fileToBase64(file, true);

    await formik.setFieldValue('documents', [
      {
        type: AdvisorDocType.CompanyAdvisorDocument,
        data: base64,
      },
    ]);
    formik.setFieldError('documents', '');

    setIsFileLoading(false);
  };

  const onFileClear = async () => {
    const current = formik.values.documents.find(
      doc => doc.type === AdvisorDocType.CompanyAdvisorDocument,
    );
    if (current?.data?.toLowerCase()?.startsWith('http')) {
      await deleteDocument({
        entity_id: data?.entity.id || 0,
        type: AdvisorDocType.CompanyAdvisorDocument,
      });
    }

    await formik.setFieldValue('documents', []);
    formik.setFieldError('documents', '');
    setDocumentUrl('');
  };

  const handleChangeCheckbox = (field: string, value: boolean) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    formik.setErrors({});
  };

  useEffect(() => {
    if (data?.entity?.document_files) {
      const currentDocument = data.entity.document_files.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      if (currentDocument) {
        setDocumentUrl(
          data.entity.document_files.find(
            ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
          )?.url || '',
        );
        formik.setFieldValue('documents', [
          {
            type: AdvisorDocType.CompanyAdvisorDocument,
            data: currentDocument?.url,
          },
        ] as Document[]);
      }
    }
  }, [data?.entity?.document_files]);

  return (
    <OnboardingWrapper
      title="Financial adviser details"
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading || isFileLoading}
    >
      <div className="w-full flex flex-col gap-8">
        <Alert
          variant="neutral-2"
          icon={AlertBlueIcon}
          label="The details below are to be completed by your financial adviser"
        />
        <CustomCheckbox
          label="I am an authorised representative or agent as nominated by the investor(s)"
          checked={formik.values.is_authorized_representative}
          onChange={e => handleChangeCheckbox('is_authorized_representative', e.target.checked)}
          error={formik.errors.is_authorized_representative}
        />
        <div className="w-full flex flex-col gap-4">
          <div className="w-full flex flex-col gap-1">
            <p className="text-[14px] leading-[22px]">
              Upload document JPG, PNG or PDF. Up to 10 mb
            </p>
            <p className="text-[12px] leading-[20px]">
              You must attach a valid authority such as Power of Attorney, guardianship order, grant
              of probate, appointment of bankruptcy etc. that is a certified copy. The document must
              be current and complete, signed by the investor or a court official and permits the
              authorised representative or agent to transact on behalf of the investor.
            </p>
          </div>
          <Dropzone
            onFileAccepted={onFileAccepted}
            onFileClear={onFileClear}
            value={documentUrl || formik.values?.documents?.[0]?.data}
            loading={isFileLoading || isDocumentDeletion}
            error={formik.errors.documents as string}
          />
        </div>
        <TextInputField
          label="Full name of authorised representative or agent"
          name="name_authorized_representative"
        />
        <TextInputField label="Role held with investor" name="role_held_with_investor" />
        <CustomCheckbox
          label="I am a financial adviser as nominated by the investor"
          checked={formik.values.is_financial_adviser_nominated_by_investor}
          onChange={e =>
            handleChangeCheckbox('is_financial_adviser_nominated_by_investor', e.target.checked)
          }
        />
        <Alert
          variant="neutral-2"
          icon={AlertBlueIcon}
          label="By completing this section of the application form, you are confirming that you are an ASIC registered financial adviser and hold a current Australian Financial Services Licence (AFSL). "
        />
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField
            label="Name of adviser"
            name="adviser.name_of_adviser"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          <TextInputField
            label="AFSL number"
            name="adviser.afsl_number"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
        </div>
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField
            label="Dealer group"
            name="adviser.dealer_group"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          <TextInputField
            label="Name pf advisory firm"
            name="adviser.name_of_advisory_firm"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
        </div>
        <TextInputField
          label="Postal address"
          name="adviser.postal_address"
          disabled={!formik.values.is_financial_adviser_nominated_by_investor}
        />
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField
            label="Suburb"
            name="adviser.suburb"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          <TextInputField
            label="postcode"
            name="adviser.postcode"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
        </div>
        <TextInputField
          label="Email address"
          name="adviser.email_address"
          disabled={!formik.values.is_financial_adviser_nominated_by_investor}
        />
        <div className="flex flex-col w-full">
          <CustomCheckbox
            label="The investor has received personal financial product advice in relation to this investment from a licence financial adviser and the advice is current"
            checked={formik.values.is_investor_received_advise}
            onChange={e => handleChangeCheckbox('is_investor_received_advise', e.target.checked)}
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          {formik.errors.is_investor_received_advise && (
            <p className="mt-1 text-sm text-[12px] text-error-red">
              {formik.errors.is_investor_received_advise}
            </p>
          )}
        </div>
      </div>
    </OnboardingWrapper>
  );
};
